"use client";
import { useEffect, useState } from "react";

import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { GTMPushEvent } from "@/src/helpers/google-tag-manager";
import setSearchId from "helpers/google-tag-manager/set-search-id";
import { formatDetailData } from "@/src/helpers/project-detail/formatters";

import ProjectHero from "@/src/components/project-detail/project-hero";
import DetailInfoScreen from "@/src/components/common/detail-info-screen";
import ProjectDetailCampains from "@/src/components/project-detail/campaigns";
import ProjectOverview from "@/src/components/project-detail/project-overview";
import Container from "@/src/components/grid/container";
import ProjectIntroduction from "@/src/components/project-detail/project-introduction";
import ProjectFeatures from "@/src/components/project-detail/project-features";
import ProjectAbout from "@/src/components/project-detail/project-about";
import HousingTypes from "@/src/components/project-detail/housing-types";
import ProjectRegionReport from "@/src/components/project-detail/region-report";
import StickyComponent from "@/src/components/project-detail/sticky-component";
import Breadcrumb from "src/components/breadcrumb";
import ProjectDetailCompanyInfo from "src/components/project-detail/project-detail-company-info";
import ProjectDetailRelatedProjects from "src/components/project-detail/project-detail-related-projects";
import { cleanAndSlugify } from "@/src/utils/string";
import ProjectCatalog from "src/components/project-detail/project-catalog";

import styles from "./styles.module.css";

const ProjectDetailScreen = ({
  pageResponse: { detailResult, districtReportDemographicInfos, priceSectionInfos },
  companysProjects,
}) => {
  const {
    id,
    ga4DataLayerList,
    name,
    company,
    campaigns,
    images,
    videoUrl,
    virtualTourUrl,
    introTitle,
    introText,
    property,
    about,
    currency,
    price,
    locationInfo,
    projectType,
    isImmediateDelivery,
    project,
    percentageOfDelivery,
    residenceType,
    percentageOfSale,
    flatTypes,
    salesStatus,
    tradeType,
    category,
    relatedProjects,
    catalogUrl,
    phoneNumber,
    showPhone,
  } = detailResult;

  const isInfoModalRendered = useSelector((state) => state.listingDetail.detailInfoModalOpened);
  const [formattedDetailData, setFormattedDetailData] = useState(() => {
    const initialData = formatDetailData(detailResult);
    return {
      ...initialData,
      imagesFullPath: flatTypes.map((item) => item.url ?? undefined),
    };
  });
  const slicedRelatedProjects = relatedProjects ? relatedProjects.slice(0, 4) : [];
  const [pageType, setPageType] = useState("project");

  useEffect(() => {
    const trackGTMEvent = async () => {
      const ga4Data = ga4DataLayerList.reduce((dataAccumulator, { key, value }) => {
        dataAccumulator[key] = value;
        return dataAccumulator;
      }, {});

      const searchId = await setSearchId("projectdetail");
      const gtmDataLayer = {
        platform: "web-desktop",
        search_id: searchId,
        ...ga4Data,
        city_id: company?.city?.id,
        town_id: company?.district?.id,
        district_id: company?.town?.id,
        project_name: name,
        project_company_name: company?.name,
        design_type: "new",
      };

      const cleanedDataLayer = cleanAndSlugify(gtmDataLayer);

      GTMPushEvent(cleanedDataLayer);
    };

    trackGTMEvent();
  }, [detailResult]);

  const updateFormattedDataImages = (componentName) => {
    setPageType(componentName === "housing-types" ? "unit-types" : "project");
    setFormattedDetailData((prevData) => ({
      ...prevData,
      imagesFullPath: componentName === "housing-types" ? flatTypes.map((item) => item.url ?? undefined) : images,
    }));
  };

  return (
    <main className={styles.wrapper}>
      <Container>
        <div className={styles.breadcrumbs}>
          <Breadcrumb data={detailResult?.breadcrumbs} />
        </div>
      </Container>
      <section aria-label="Proje Detayları" data-section-name="hero">
        <ProjectHero
          name={name}
          companyName={company?.name}
          currency={currency}
          price={price}
          images={images}
          videoUrl={videoUrl}
          virtualTourUrl={virtualTourUrl}
          updateFormattedDataImages={updateFormattedDataImages}
        />
      </section>
      <Container className={styles.container}>
        <section aria-label="Kampanyalar" data-section-name="campaigns">
          <ProjectDetailCampains campaigns={campaigns} projectName={name} />
        </section>
        <section aria-label="Konut Tipleri" data-section-name="housing-types">
          <HousingTypes
            unitTypes={formattedDetailData.flatTypes}
            company={formattedDetailData.company}
            city={locationInfo?.city}
            district={locationInfo?.district}
            updateFormattedDataImages={updateFormattedDataImages}
            projectId={id}
            formObject={formattedDetailData.formData}
            salesStatus={salesStatus}
          />
        </section>
        <section aria-label="Genel Özellikler" data-section-name="overview">
          <ProjectOverview
            locationInfo={locationInfo}
            projectType={projectType}
            residenceType={residenceType}
            isImmediateDelivery={isImmediateDelivery}
            date={property?.deliveryDate}
            roomCount={project?.ROOM_COUNT}
            unitCount={property?.flatCount}
            percentageOfDelivery={percentageOfDelivery}
            percentageOfSale={percentageOfSale}
          />
        </section>
        <section aria-label="Proje Tanıtımı" data-section-name="introduction">
          <ProjectIntroduction introTitle={introTitle} introText={introText} />
        </section>
        <section aria-label="Proje Özellikleri" data-section-name="features">
          <ProjectFeatures features={property?.features} />
        </section>
        <section aria-label="Proje Hakkında" data-section-name="about">
          <ProjectAbout about={about} name={name} />
        </section>
        {catalogUrl && 
        <section aria-label="Proje Katalogu" data-section-name="catalog">
          <ProjectCatalog catalogUrl={catalogUrl} projectName={name} />
        </section>
        }
        <section aria-label="Bölge raporu" data-section-name="districtReport">
          {(districtReportDemographicInfos || priceSectionInfos) && (
            <ProjectRegionReport
              districtReportDemographicInfos={districtReportDemographicInfos}
              isProjectDetail={true}
              listingData={detailResult}
              unitTypes={formattedDetailData.flatTypes}
              districtReportPriceInfos={priceSectionInfos && priceSectionInfos}
            />
          )}
        </section>
        <section aria-label="Firma Künyesi" data-section-name="companyInfo">
          <ProjectDetailCompanyInfo companyDetail={company} companysProjects={companysProjects} />
        </section>
      </Container>
      <div className={styles.contactFormWrapper}>
        <StickyComponent
          projectDetailFormData={formattedDetailData.formData}
          salesStatus={salesStatus}
          projectCity={locationInfo?.city?.slug}
          tradeType={tradeType}
          category={category}
          hasRelatedProjects={relatedProjects?.length > 0}
          phoneNumber={phoneNumber}
          showPhone={showPhone}
        />
      </div>
      <section aria-label="Bölgedeki Projeler" data-section-name="relatedProjects">
        {slicedRelatedProjects.length > 0 ? (
          <ProjectDetailRelatedProjects relatedProjects={slicedRelatedProjects} />
        ) : (
          <div className={styles.space} />
        )}
      </section>
      {isInfoModalRendered && <DetailInfoScreen data={formattedDetailData} pageType={pageType} />}
    </main>
  );
};

ProjectDetailScreen.propTypes = {
  pageResponse: PropTypes.object.isRequired,
  companysProjects: PropTypes.object.isRequired,
};

export default ProjectDetailScreen;
